import { supabase } from '../config/supabase';

const DEFAULT_MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;
const MAX_IMAGE_DIMENSION = 2200;
const MIN_IMAGE_DIMENSION = 480;
const VALID_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
const IMAGE_QUALITY_LEVELS = [0.92, 0.84, 0.76, 0.68, 0.6, 0.52, 0.44, 0.36];
const IMAGE_DIMENSION_TARGETS = [2200, 1800, 1600, 1400, 1200, 1000, 800, 640, 480];
const STORAGE_RETRY_TARGET_SIZES = [
  DEFAULT_MAX_IMAGE_SIZE_BYTES,
  2 * 1024 * 1024,
  1024 * 1024,
  512 * 1024,
];
const IMAGE_EXTENSION_BY_TYPE: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
};

export const CLIENT_BRANDING_BUCKET = "client-branding-assets";
export type ClientBrandingImageKind = "logo-print" | "logo-mark" | "logo-wordmark";

const validateImageFile = (file: File) => {
  if (!VALID_IMAGE_TYPES.includes(file.type)) {
    throw new Error("Tipo de arquivo inválido. Use apenas JPG, PNG ou WebP.");
  }
};

const loadImage = (file: File): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    const objectUrl = URL.createObjectURL(file);

    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Não foi possível ler a imagem selecionada."));
    };

    image.src = objectUrl;
  });

const canvasToBlob = (
  canvas: HTMLCanvasElement,
  type: string,
  quality?: number,
): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error("Não foi possível processar a imagem selecionada."));
        return;
      }

      resolve(blob);
    }, type, quality);
  });

const getResizedDimensions = (width: number, height: number, maxDimension: number) => {
  if (Math.max(width, height) <= maxDimension) {
    return { width, height };
  }

  const scale = maxDimension / Math.max(width, height);
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
};

const getOptimizedImageType = (fileType: string) =>
  fileType === "image/png" ? "image/webp" : fileType;

const createOptimizedFile = (file: File, blob: Blob, type: string) => {
  const originalName = file.name.replace(/\.[^.]+$/, '');
  const extension = IMAGE_EXTENSION_BY_TYPE[type] ?? "jpg";

  return new File([blob], `${originalName}.${extension}`, {
    type,
    lastModified: Date.now(),
  });
};

const formatBytesForMessage = (bytes: number) => {
  const sizeInMB = bytes / (1024 * 1024);
  if (sizeInMB >= 1) {
    const rounded = Number.isInteger(sizeInMB) ? sizeInMB.toString() : sizeInMB.toFixed(1);
    return `${rounded}MB`;
  }

  return `${Math.round(bytes / 1024)}KB`;
};

const getResizeTargets = (width: number, height: number) => {
  const longestSide = Math.max(width, height);
  const initialTarget = Math.min(longestSide, MAX_IMAGE_DIMENSION);
  const targets = [initialTarget, ...IMAGE_DIMENSION_TARGETS.filter((target) => target < initialTarget)];

  if (!targets.includes(MIN_IMAGE_DIMENSION)) {
    targets.push(MIN_IMAGE_DIMENSION);
  }

  return Array.from(
    new Set(targets.map((target) => Math.max(1, Math.min(target, initialTarget)))),
  );
};

const getErrorMessage = (error: unknown) => {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'object' && error && 'message' in error && typeof error.message === 'string') {
    return error.message;
  }

  return String(error);
};

const isStorageSizeLimitError = (error: unknown) =>
  /maximum allowed size|exceeded the maximum allowed size/i.test(getErrorMessage(error));

export const prepareImageForUpload = async (
  file: File,
  maxSizeBytes = DEFAULT_MAX_IMAGE_SIZE_BYTES,
): Promise<File> => {
  validateImageFile(file);

  if (file.size <= maxSizeBytes) {
    return file;
  }

  const image = await loadImage(file);
  const targetType = getOptimizedImageType(file.type);
  const resizeTargets = getResizeTargets(image.width, image.height);
  let smallestBlob: Blob | null = null;

  for (const resizeTarget of resizeTargets) {
    const { width, height } = getResizedDimensions(image.width, image.height, resizeTarget);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error("Não foi possível processar a imagem selecionada.");
    }

    context.drawImage(image, 0, 0, width, height);

    for (const quality of IMAGE_QUALITY_LEVELS) {
      const blob = await canvasToBlob(canvas, targetType, quality);

      if (!smallestBlob || blob.size < smallestBlob.size) {
        smallestBlob = blob;
      }

      if (blob.size <= maxSizeBytes) {
        return createOptimizedFile(file, blob, targetType);
      }
    }
  }

  if (smallestBlob && smallestBlob.size <= maxSizeBytes) {
    return createOptimizedFile(file, smallestBlob, targetType);
  }

  throw new Error(
    `Não foi possível reduzir a imagem abaixo de ${formatBytesForMessage(maxSizeBytes)}. Tente uma imagem menor.`,
  );
};

const buildStorageFileName = (folder: string, file: File) => {
  const timestamp = Date.now();
  const randomSuffix = Math.random().toString(36).slice(2, 8);
  const extension = IMAGE_EXTENSION_BY_TYPE[file.type] ?? "jpg";

  return `${folder}/${timestamp}-${randomSuffix}.${extension}`;
};

const uploadImageAsset = async (
  bucket: string,
  folder: string,
  file: File,
  errorLabel: string,
): Promise<{ path: string; publicUrl: string }> => {
  let lastError: unknown = null;

  for (const targetSize of STORAGE_RETRY_TARGET_SIZES) {
    const preparedFile = await prepareImageForUpload(file, targetSize);
    const fileName = buildStorageFileName(folder, preparedFile);

    const { error: uploadError } = await supabase.storage
      .from(bucket)
      .upload(fileName, preparedFile, { cacheControl: "3600", upsert: false });

    if (!uploadError) {
      const { data } = supabase.storage.from(bucket).getPublicUrl(fileName);
      if (!data) {
        throw new Error(errorLabel);
      }

      return {
        path: fileName,
        publicUrl: data.publicUrl,
      };
    }

    lastError = uploadError;

    if (!isStorageSizeLimitError(uploadError)) {
      throw uploadError;
    }
  }

  if (isStorageSizeLimitError(lastError)) {
    throw new Error("A imagem excede o limite configurado no bucket de armazenamento. Tente uma imagem menor.");
  }

  throw lastError ?? new Error(errorLabel);
};

const uploadImage = async (bucket: string, folder: string, file: File, errorLabel: string): Promise<string> => {
  const asset = await uploadImageAsset(bucket, folder, file, errorLabel);
  return asset.publicUrl;
};

export const getClientBrandingPublicUrl = (path: string): string => {
  const { data } = supabase.storage.from(CLIENT_BRANDING_BUCKET).getPublicUrl(path);
  return data?.publicUrl ?? '';
};

export const uploadClientBrandingImage = async (
  clientId: string,
  kind: ClientBrandingImageKind,
  file: File,
): Promise<{ path: string; publicUrl: string }> => {
  try {
    return await uploadImageAsset(
      CLIENT_BRANDING_BUCKET,
      `${clientId}/${kind}`,
      file,
      "Erro ao obter URL pública da identidade visual da clínica.",
    );
  } catch (error) {
    console.error("Error uploading client branding image:", error);
    throw error;
  }
};

export const getStorageImageURL = async (path: string): Promise<string> => {
  try {
    const { data } = supabase.storage.from("course-images").getPublicUrl(path);
    if (!data) {
      throw new Error("Erro ao obter URL pública da imagem.");
    }
    return data.publicUrl;
  } catch (error) {
    console.error('Error getting image URL:', error);
    throw error;
  }
};

export const uploadNewsImage = async (file: File): Promise<string> => {
  try {
    return await uploadImage(
      "news-images",
      "news",
      file,
      "Erro ao obter URL pública da imagem da notícia.",
    );
  } catch (error) {
    console.error("Error uploading news image:", error);
    throw error;
  }
};

export const uploadCourseImage = async (file: File): Promise<string> => {
  try {
    return await uploadImage(
      "course-images",
      "courses",
      file,
      "Erro ao obter URL pública da imagem do curso.",
    );
  } catch (error) {
    console.error("Error uploading course image:", error);
    throw error;
  }
};
